import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "@phosphor-icons/react";
import PageHero from "../components/PageHero";
import { IMAGES } from "../data";

const STAGES = [
  {
    n: "01",
    title: "Land Acquisition",
    body:
      "We acquire fertile, chemical-free land in proven agricultural belts. Each parcel is profiled for soil chemistry, water table, and microclimate before a single seed is planted.",
    img: IMAGES.field,
  },
  {
    n: "02",
    title: "Moringa & Crop Cultivation",
    body:
      "Moringa is grown to a pharmaceutical-grade specification — no synthetic pesticides, no chemical fertilizers. Multi-cut fodder grasses and pulses round out the feed program.",
    img: IMAGES.moringa,
  },
  {
    n: "03",
    title: "Cattle Breeding & Feeding",
    body:
      "Indigenous Gir and Sahiwal cattle are paired with high-yield imported breeds. Open-grazing, in-house veterinary oversight, and proprietary Moringa-based feed produce a high-fat, high-solids milk profile.",
    img: IMAGES.herd,
  },
  {
    n: "04",
    title: "Milk Production & Cold Chain",
    body:
      "Milk moves directly from milking parlour to chilled storage on our own infrastructure. No third-party aggregation, no transit deviation, no quality drift.",
    img: IMAGES.cattle,
  },
  {
    n: "05",
    title: "Processing & Manufacturing",
    body:
      "Paneer, ghee, cheese, butter, yogurt, curd, ice cream and milk powder — every value-added product is made on site, on dedicated lines tuned to institutional spec.",
    img: IMAGES.facility,
  },
  {
    n: "06",
    title: "Quality Testing",
    body:
      "Lot-level testing for fat, SNF, microbial load, antibiotic residue and adulteration. Every shipment leaves with a Certificate of Analysis (COA).",
    img: IMAGES.lab,
  },
  {
    n: "07",
    title: "Packaging",
    body:
      "Retail, HORECA, and 25kg export-grade formats. Custom private-label runs and multi-language exports handled in house.",
    img: IMAGES.cheese,
  },
  {
    n: "08",
    title: "Distribution & Export",
    body:
      "FSSAI- and APEDA-aligned cold chain. Documentation, phyto-sanitary, halal and country-specific compliance issued per shipment to 14 destinations.",
    img: IMAGES.logistics,
  },
];

export default function Process() {
  return (
    <div data-testid="process-page">
      <PageHero
        overline="From Farm To Product"
        title={
          <>
            Eight stages.
            <br />
            <span className="italic text-terracotta">One owner.</span>
          </>
        }
        subtitle="A walkthrough of our closed-loop supply chain — built for investor confidence and institutional buyer trust."
        image={IMAGES.facility}
        testId="process-hero"
      />

      <section className="max-w-8xl mx-auto px-6 lg:px-12 py-20 lg:py-28" data-testid="process-stages">
        <div className="space-y-24 lg:space-y-32">
          {STAGES.map((s, i) => (
            <div
              key={s.n}
              className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center ${
                i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
              }`}
              data-testid={`process-stage-${s.n}`}
            >
              <div className="lg:col-span-6 relative">
                <img
                  src={s.img}
                  alt={s.title}
                  className="w-full h-[380px] lg:h-[460px] object-cover"
                />
                <div className="absolute -top-4 -left-4 lg:-top-6 lg:-left-6 bg-bone border border-olive/15 px-5 py-3 font-mono text-xs uppercase tracking-widest2 text-terracotta">
                  Stage {s.n}
                </div>
              </div>
              <div className="lg:col-span-6">
                <h2 className="h-section text-4xl md:text-5xl lg:text-6xl">
                  {s.title}
                </h2>
                <p className="mt-8 text-lg text-olive-soft leading-relaxed max-w-xl">
                  {s.body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-olive text-bone py-24" data-testid="process-cta-section">
        <div className="max-w-8xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-8">
            <span className="overline text-terracotta">Process Audit</span>
            <h2 className="font-heading text-4xl md:text-5xl mt-6 leading-tight text-bone max-w-3xl">
              Want a tour? We host institutional buyers and investors at the
              farm and the facility.
            </h2>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link to="/contact" className="btn-primary" data-testid="process-cta-tour">
              Schedule a Visit
              <ArrowUpRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
